// src/stores/auth.ts
import { defineStore } from 'pinia'
import { validCurrUser } from '@/utils/api/auth'
import { pickAvatar } from '@/utils/avatar'
import { isValidJWTFormat } from '@/utils/jwt'

export interface User {
  username: string
  avatar: string
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: null as string | null,     // accessToken（用于鉴权）
    user: null as User | null,        // UI 展示用
    _lastValidAt: 0,                  // 校验缓存时间戳
  }),

  getters: {
    isAuthed: (s) => Boolean(s.token || s.user),
  },

  actions: {
    initFromStorage() {
      const clearAuthStorage = () => {
        this.token = null
        this.user = null
        localStorage.removeItem('auth')
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('tokenType')
        localStorage.removeItem('expiresAt')
        localStorage.removeItem('token') // 兼容历史遗留
      }

      // 1) 优先用 accessToken 恢复鉴权态（但必须校验 JWT 格式）
      const accessToken = localStorage.getItem('accessToken')
      if (accessToken) {
        if (isValidJWTFormat(accessToken)) {
          this.token = accessToken
        } else {
          // accessToken 是脏数据（比如 mock-token）
          clearAuthStorage()
          return
        }
      }

      // 2) 恢复用户展示信息（头像/用户名）
      const raw = localStorage.getItem('auth')
      if (!raw) return

      try {
        const parsed = JSON.parse(raw) as { token?: string; user?: User }

        // 2.1 如果 accessToken 不存在，但 auth.token 存在，则尝试用它兜底恢复
        if (!this.token && parsed.token) {
          if (isValidJWTFormat(parsed.token)) {
            this.token = parsed.token
            localStorage.setItem('accessToken', parsed.token) // 同步一份，便于 axios 统一读取
          } else {
            clearAuthStorage()
            return
          }
        }

        // 2.2 用户信息可恢复（仅用于 UI 展示）
        if (parsed.user) this.user = parsed.user
      } catch {
        // auth 解析失败：清掉避免污染
        clearAuthStorage()
      }
    },


    login(accessToken: string, user: User) {
      this.token = accessToken
      this.user = user
      this._lastValidAt = Date.now()

      localStorage.setItem('accessToken', accessToken)
      localStorage.setItem('auth', JSON.stringify({ token: accessToken, user }))
    },

    logout() {
      this.token = null
      this.user = null
      this._lastValidAt = 0

      localStorage.removeItem('auth')
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('tokenType')
      localStorage.removeItem('expiresAt')

      // 清理历史遗留 key
      localStorage.removeItem('token')
    },

    async validateCurrUser(force = false): Promise<boolean> {
      // 没 token 就别校验了
      const accessToken = localStorage.getItem('accessToken')
      if (!accessToken) {
        this.logout()
        return false
      }

      // 30 秒内不重复校验
      if (!force && this._lastValidAt && Date.now() - this._lastValidAt < 1_000) {
        return true
      }

      try {
        const res = await validCurrUser()
        // 你接口返回：{ code: 1, data: { valid: true, userInfo: {...} } }
        if (res?.code === 1 && res?.data?.valid) {
          const username =
            res.data.userInfo?.username ||
            this.user?.username ||
            ''

          // 更新 UI user（你只需要 username/avatar 就够）
          if (username) {
            this.user = { username, avatar: pickAvatar(username) }
          }

          // token 继续以 localStorage 为准
          this.token = accessToken
          this._lastValidAt = Date.now()
          return true
        }

        // 接口明确返回未登录
        this.logout()
        return false
      } catch {
        // 校验接口异常：按未登录处理（你也可以改成“先不登出”）
        this.logout()
        return false
      }
    },
  },
})
